import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SectionChat } from '@/components/ui/SectionChat';
import { functionsService } from '@/services/functionsService';
import { firestoreService } from '@/services/firestoreService';
import { useCredits } from '@/contexts/CreditsContext';
import { exportAnalysisToPDF } from '@/utils/export';

interface FilingAnalysis {
  summary: string;
  keyMetrics: Record<string, string>;
  segmentBreakdown: Array<{
    segment: string;
    revenue?: string;
    growth?: string;
    notes: string;
  }>;
  litigationAndRisks: {
    litigation: string[];
    risks: string[];
  };
  forwardLookingStatements: string[];
  yearOverYearChanges: Array<{
    metric: string;
    current: string;
    previous: string;
    change: string;
    percentage: string;
  }>;
}

interface Filing {
  id: string;
  companyId: string;
  companyName: string;
  ticker: string;
  filingType: string;
  date: any;
  text: string;
  sourceUrl?: string;
}

interface FilingViewerProps {
  filingId: string;
}

export const FilingViewer: React.FC<FilingViewerProps> = ({ filingId }) => {
  const [filing, setFiling] = useState<Filing | null>(null);
  const [analysis, setAnalysis] = useState<FilingAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'metrics' | 'segments' | 'risks' | 'forward' | 'yoy' | 'filing'>('summary');
  const [analysisDocId, setAnalysisDocId] = useState<string | null>(null);
  const { balance, refreshBalance } = useCredits();

  useEffect(() => {
    loadFiling();
  }, [filingId]);

  const loadFiling = async () => {
    try {
      setLoading(true);
      const filingData = await firestoreService.getDocument<Filing>('filings', filingId);
      
      if (!filingData) {
        throw new Error('Filing not found');
      }

      setFiling(filingData);

      const existingAnalysis = await firestoreService.getDocuments<{ id: string; analysis?: FilingAnalysis; filingId?: string }>('filingAnalyses', [
        firestoreService.where('filingId', '==', filingId),
        firestoreService.limit(1),
      ]);

      if (existingAnalysis.length > 0) {
        const first = existingAnalysis[0];
        setAnalysis(first.analysis ?? null);
        setAnalysisDocId(first.id);
      }
    } catch (error) {
      console.error('Failed to load filing:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasFilingContent = Boolean(filing?.text?.trim());
  const canAnalyze = hasFilingContent || Boolean(filing?.sourceUrl);
  const isTenK = (filing?.filingType || '').toUpperCase().includes('10-K');
  const availableTabs: Array<'summary' | 'metrics' | 'segments' | 'risks' | 'forward' | 'yoy' | 'filing'> = isTenK
    ? ['summary', 'filing']
    : ['summary', 'metrics', 'segments', 'risks', 'forward', 'yoy', 'filing'];

  const handleAnalyze = async () => {
    if (!filing || balance < 10) {
      alert('Insufficient credits. You need at least 10 credits to analyze a filing.');
      return;
    }
    if (!canAnalyze) {
      alert('This filing has no stored text and no source link. Analysis is not available.');
      return;
    }

    try {
      setAnalyzing(true);
      const result = await functionsService.analyzeFiling(filingId);
      setAnalysis(result.analysis);
      await refreshBalance();
      await loadFiling();
    } catch (error: any) {
      const msg = error?.message || 'Analysis failed.';
      alert(msg.includes('INVALID_DATA') || msg.includes('could not be fetched')
        ? 'Filing text could not be loaded. You can view the filing on SEC.gov using the link in the Filing Content tab.'
        : `Failed to analyze: ${msg}`);
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (!filing) {
    return (
      <Card>
        <div className="text-center py-12">
          <p className="body-text text-gray-600">Filing not found</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="h2 text-2xl mb-1">{filing.companyName}</h2>
            <p className="body-text text-gray-600">
              {filing.ticker} • {filing.filingType} • {new Date(filing.date?.toDate?.() || filing.date).toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-2">
            {!analysis && (
              <Button
                variant="primary"
                onClick={handleAnalyze}
                isLoading={analyzing}
                disabled={balance < 10 || !canAnalyze}
                title={!canAnalyze ? 'No filing content or SEC link available' : undefined}
              >
                Analyze (10 credits)
              </Button>
            )}
            {analysis && (
              <Button
                variant="outline"
                onClick={() => exportAnalysisToPDF(analysis, `${filing.companyName} - ${filing.filingType}`)}
              >
                Export PDF
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-blazerex-green text-blazerex-green font-header-bold'
                  : 'border-transparent text-gray-600 hover:text-blazerex-green'
              }`}
            >
              {tab === 'yoy' ? 'YoY Changes' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </Card>

      {!analysis && activeTab !== 'filing' ? (
        <Card>
          <div className="text-center py-12">
            <p className="body-text text-gray-600 mb-4">
              This filing hasn't been analyzed yet.
            </p>
            <Button
              variant="primary"
              onClick={handleAnalyze}
              disabled={balance < 10 || !canAnalyze}
              title={!canAnalyze ? 'No filing content or SEC link' : undefined}
            >
              Analyze Now (10 credits)
            </Button>
            {!canAnalyze && (
              <p className="body-text text-sm text-amber-700 mt-2">Analysis requires stored filing text or an SEC.gov link.</p>
            )}
          </div>
        </Card>
      ) : (
        <>
          {activeTab === 'summary' && analysis && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Summary</h3>
                <p className="body-text text-gray-700 whitespace-pre-wrap">{analysis.summary}</p>
              </Card>
              <SectionChat
                sectionContent={analysis.summary}
                sectionTitle="this summary"
                context={{ filingId: analysisDocId ?? undefined, companyId: filing?.companyId }}
              />
            </>
          )}

          {activeTab === 'metrics' && analysis && !isTenK && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Key Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(analysis.keyMetrics).map(([key, value]) => (
                    <div key={key} className="p-4 bg-gray-50 rounded-lg">
                      <p className="body-text text-sm text-gray-600 mb-1">{key}</p>
                      <p className="sub-header text-lg">{value}</p>
                    </div>
                  ))}
                </div>
              </Card>
              <SectionChat
                sectionContent={Object.entries(analysis.keyMetrics).map(([k, v]) => `${k}: ${v}`).join('\n')}
                sectionTitle="Key Metrics"
                context={{ filingId: analysisDocId ?? undefined, companyId: filing?.companyId }}
              />
            </>
          )}

          {activeTab === 'segments' && analysis && !isTenK && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Segment Breakdown</h3>
                <div className="space-y-4">
                  {analysis.segmentBreakdown.map((segment, i) => (
                    <div key={i} className="border-b border-gray-200 pb-4 last:border-0">
                      <h4 className="sub-header text-lg mb-2">{segment.segment}</h4>
                      {segment.revenue && (
                        <p className="body-text text-gray-700 mb-1">Revenue: {segment.revenue}</p>
                      )}
                      {segment.growth && (
                        <p className="body-text text-gray-700 mb-1">Growth: {segment.growth}</p>
                      )}
                      <p className="body-text text-gray-600">{segment.notes}</p>
                    </div>
                  ))}
                </div>
              </Card>
              <SectionChat
                sectionContent={analysis.segmentBreakdown
                  .map((s) => `${s.segment}: ${s.revenue ?? 'N/A'} | ${s.growth ?? 'N/A'} - ${s.notes}`)
                  .join('\n')}
                sectionTitle="Segment Breakdown"
                context={{ filingId: analysisDocId ?? undefined, companyId: filing?.companyId }}
              />
            </>
          )}

          {activeTab === 'risks' && analysis && !isTenK && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <h3 className="sub-header text-xl mb-4 text-red-600">Litigation</h3>
                  <ul className="space-y-2">
                    {analysis.litigationAndRisks.litigation.length > 0 ? (
                      analysis.litigationAndRisks.litigation.map((item, i) => (
                        <li key={i} className="body-text text-gray-700 flex items-start gap-2">
                          <span className="text-red-500 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))
                    ) : (
                      <li className="body-text text-gray-500">No litigation items</li>
                    )}
                  </ul>
                </Card>
                <Card>
                  <h3 className="sub-header text-xl mb-4 text-orange-600">Risks</h3>
                  <ul className="space-y-2">
                    {analysis.litigationAndRisks.risks.map((risk, i) => (
                      <li key={i} className="body-text text-gray-700 flex items-start gap-2">
                        <span className="text-orange-500 mt-1">•</span>
                        <span>{risk}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
              <SectionChat
                sectionContent={[
                  'Litigation:',
                  ...analysis.litigationAndRisks.litigation,
                  'Risks:',
                  ...analysis.litigationAndRisks.risks,
                ].join('\n')}
                sectionTitle="Risks & Litigation"
                context={{ filingId: analysisDocId ?? undefined, companyId: filing?.companyId }}
              />
            </>
          )}

          {activeTab === 'forward' && analysis && !isTenK && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Forward-Looking Statements</h3>
                <ul className="space-y-3">
                  {analysis.forwardLookingStatements.map((statement, i) => (
                    <li key={i} className="body-text text-gray-700 flex items-start gap-3">
                      <span className="text-blazerex-green mt-1">→</span>
                      <span>{statement}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <SectionChat
                sectionContent={analysis.forwardLookingStatements.join('\n')}
                sectionTitle="Forward-Looking Statements"
                context={{ filingId: analysisDocId ?? undefined, companyId: filing?.companyId }}
              />
            </>
          )}

          {activeTab === 'yoy' && analysis && !isTenK && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Year-over-Year Changes</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 sub-header">Metric</th>
                        <th className="text-right py-3 px-4 sub-header">Current</th>
                        <th className="text-right py-3 px-4 sub-header">Previous</th>
                        <th className="text-right py-3 px-4 sub-header">Change</th>
                        <th className="text-right py-3 px-4 sub-header">% Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.yearOverYearChanges.map((change, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-3 px-4 body-text">{change.metric}</td>
                          <td className="py-3 px-4 text-right body-text">{change.current}</td>
                          <td className="py-3 px-4 text-right body-text text-gray-500">{change.previous}</td>
                          <td className="py-3 px-4 text-right body-text">{change.change}</td>
                          <td className={`py-3 px-4 text-right body-text font-medium ${
                            parseFloat(change.percentage) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {change.percentage}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <SectionChat
                sectionContent={analysis.yearOverYearChanges
                  .map((c) => `${c.metric}: ${c.current} (prev: ${c.previous}, ${c.percentage})`)
                  .join('\n')}
                sectionTitle="Year-over-Year Changes"
                context={{ filingId: analysisDocId ?? undefined, companyId: filing?.companyId }}
              />
            </>
          )}

          {activeTab === 'filing' && (
            <Card>
              <h3 className="sub-header text-xl mb-4">Filing Content</h3>
              {filing.text && filing.text.trim() ? (
                <div className="bg-gray-50 p-6 rounded-lg max-h-96 overflow-y-auto">
                  <pre className="body-text text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {filing.text}
                  </pre>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
                  <p className="body-text text-amber-800 mb-2">Full filing text is not stored in the app.</p>
                  {filing.sourceUrl ? (
                    <>
                      <a
                        href={filing.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blazerex-green hover:underline font-medium"
                      >
                        View this filing on SEC.gov →
                      </a>
                      <p className="body-text text-sm text-amber-700 mt-2">
                        You can still run &quot;Analyze&quot; — we will fetch the filing from SEC to generate the summary.
                      </p>
                    </>
                  ) : (
                    <p className="body-text text-sm text-amber-700">Analysis is not available for this filing (no content or SEC link).</p>
                  )}
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

